/**
 * Error logging service for global error handling
 */

import { ErrorDetails, ErrorLogEntry, ErrorSeverity, ErrorCategory } from '../types/error';

export class ErrorLogger {
  private static instance: ErrorLogger;
  private logs: ErrorLogEntry[] = [];
  private maxLogs = 1000;
  private enableConsoleLogging = true;
  private enableRemoteLogging = false;
  private remoteEndpoint?: string;

  private constructor() {
    // Initialize logger
    this.setupErrorListeners();
  }

  public static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  /**
   * Setup global error listeners
   */
  private setupErrorListeners(): void {
    // Global error handler for unhandled errors
    window.addEventListener('error', (event) => {
      this.logError({
        id: this.generateErrorId(),
        message: event.error?.message || event.message || 'Unknown error',
        stack: event.error?.stack,
        name: event.error?.name || 'Error',
        cause: event.error?.cause,
        severity: ErrorSeverity.HIGH,
        category: ErrorCategory.SYSTEM,
        recoverable: false,
        context: {
          route: window.location.pathname,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          environment: import.meta.env.MODE,
        }
      });
    });

    // Global promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        id: this.generateErrorId(),
        message: event.reason?.message || 'Unhandled promise rejection',
        stack: event.reason?.stack,
        name: event.reason?.name || 'UnhandledPromiseRejection',
        cause: event.reason?.cause,
        severity: ErrorSeverity.HIGH,
        category: ErrorCategory.SYSTEM,
        recoverable: false,
        context: {
          route: window.location.pathname,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          environment: import.meta.env.MODE,
        }
      });
    });
  }

  /**
   * Log an error with full context
   */
  public logError(errorDetails: ErrorDetails): void {
    const logEntry: ErrorLogEntry = {
      id: errorDetails.id,
      timestamp: new Date().toISOString(),
      error: errorDetails,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId(),
    };

    // Add to local logs
    this.logs.push(logEntry);
    
    // Maintain max logs limit
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Console logging
    if (this.enableConsoleLogging) {
      this.logToConsole(errorDetails);
    }

    // Remote logging
    if (this.enableRemoteLogging && this.remoteEndpoint) {
      this.logToRemote(logEntry);
    }

    // Store in localStorage for persistence
    this.persistLogs();
  }

  /**
   * Log to console with appropriate level
   */
  private logToConsole(errorDetails: ErrorDetails): void {
    const logData = {
      id: errorDetails.id,
      message: errorDetails.message,
      category: errorDetails.category,
      severity: errorDetails.severity,
      stack: errorDetails.stack,
      context: errorDetails.context,
    };

    switch (errorDetails.severity) {
      case ErrorSeverity.CRITICAL:
        console.error('🚨 CRITICAL ERROR:', logData);
        break;
      case ErrorSeverity.HIGH:
        console.error('❌ HIGH SEVERITY ERROR:', logData);
        break;
      case ErrorSeverity.MEDIUM:
        console.warn('⚠️ MEDIUM SEVERITY ERROR:', logData);
        break;
      case ErrorSeverity.LOW:
        console.info('ℹ️ LOW SEVERITY ERROR:', logData);
        break;
      default:
        console.log('📝 ERROR:', logData);
    }
  }

  /**
   * Send error to remote logging service
   */
  private async logToRemote(logEntry: ErrorLogEntry): Promise<void> {
    if (!this.remoteEndpoint) return;

    try {
      await fetch(this.remoteEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logEntry),
      });
    } catch (error) {
      console.warn('Failed to send error log to remote service:', error);
    }
  }

  /**
   * Persist logs to localStorage
   */
  private persistLogs(): void {
    try {
      const recentLogs = this.logs.slice(-100); // Keep last 100 logs
      localStorage.setItem('error_logs', JSON.stringify(recentLogs));
    } catch (error) {
      console.warn('Failed to persist error logs:', error);
    }
  }

  /**
   * Load logs from localStorage
   */
  public loadPersistedLogs(): void {
    try {
      const persistedLogs = localStorage.getItem('error_logs');
      if (persistedLogs) {
        const logs = JSON.parse(persistedLogs);
        this.logs = [...logs, ...this.logs];
      }
    } catch (error) {
      console.warn('Failed to load persisted error logs:', error);
    }
  }

  /**
   * Generate unique error ID
   */
  public generateErrorId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current user ID from auth store
   */
  private getCurrentUserId(): string | undefined {
    try {
      const authData = localStorage.getItem('auth-storage');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.state?.user?.id;
      }
    } catch (error) {
      // Ignore error
    }
    return undefined;
  }

  /**
   * Get or create session ID
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Get all error logs
   */
  public getLogs(): ErrorLogEntry[] {
    return [...this.logs];
  }

  /**
   * Get logs by category
   */
  public getLogsByCategory(category: ErrorCategory): ErrorLogEntry[] {
    return this.logs.filter(log => log.error.category === category);
  }

  /**
   * Get logs by severity
   */
  public getLogsBySeverity(severity: ErrorSeverity): ErrorLogEntry[] {
    return this.logs.filter(log => log.error.severity === severity);
  }

  /**
   * Clear all logs
   */
  public clearLogs(): void {
    this.logs = [];
    localStorage.removeItem('error_logs');
  }

  /**
   * Export logs as JSON
   */
  public exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Configure logger settings
   */
  public configure(options: {
    enableConsoleLogging?: boolean;
    enableRemoteLogging?: boolean;
    remoteEndpoint?: string;
    maxLogs?: number;
  }): void {
    if (options.enableConsoleLogging !== undefined) {
      this.enableConsoleLogging = options.enableConsoleLogging;
    }
    if (options.enableRemoteLogging !== undefined) {
      this.enableRemoteLogging = options.enableRemoteLogging;
    }
    if (options.remoteEndpoint !== undefined) {
      this.remoteEndpoint = options.remoteEndpoint;
    }
    if (options.maxLogs !== undefined) {
      this.maxLogs = options.maxLogs;
    }
  }

  /**
   * Create error details from Error object
   */
  public createErrorDetails(
    error: Error, 
    category: ErrorCategory = ErrorCategory.UNKNOWN,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    recoverable: boolean = true
  ): ErrorDetails {
    return {
      id: this.generateErrorId(),
      message: error.message,
      stack: error.stack,
      name: error.name,
      cause: (error as any).cause,
      severity,
      category,
      recoverable,
      context: {
        route: window.location.pathname,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        environment: import.meta.env.MODE,
        userId: this.getCurrentUserId(),
        sessionId: this.getSessionId(),
      }
    };
  }
}

// Export singleton instance
export const errorLogger = ErrorLogger.getInstance();